import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import CartDrawer from '@/components/CartDrawer';
import { Menu, X, LogIn, UserCircle2, ShoppingBag } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/catalog', label: 'Catalogue' },
  { to: '/store', label: 'Store' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

function DesktopConsoleShortcut() {
  const { user } = useAuth();
  const { totals, openDrawer } = useCart();
  const cartBtn = (
    <button
      type="button"
      onClick={openDrawer}
      className="relative inline-flex items-center justify-center rounded-full w-9 h-9 border transition hover:bg-[rgba(75,24,56,0.06)]"
      style={{ borderColor: 'rgba(75,24,56,0.2)', color: 'var(--heritage-plum)' }}
      aria-label="Open shopping bag"
      data-testid="nav-cart"
    >
      <ShoppingBag size={16} />
      {totals.itemCount > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium flex items-center justify-center"
          style={{ background: 'var(--heritage-plum)', color: '#F6F0E4' }}
          data-testid="nav-cart-badge"
        >
          {totals.itemCount}
        </span>
      )}
    </button>
  );
  if (!user) {
    return (
      <div className="hidden md:flex items-center gap-2 shrink-0">
        {cartBtn}
        <Link to="/login" className="h-btn-outline px-4 py-2 text-xs" data-testid="nav-login">Log in</Link>
        <Link to="/store" className="h-btn px-4 py-2 text-xs" data-testid="nav-shop">Shop Store</Link>
      </div>
    );
  }
  const dest =
    user.role === 'admin' ? '/admin' :
    user.role === 'founder' ? '/console/founder' :
    user.role === 'ambassador' ? '/console/ambassador' :
    '/console/customer';
  return (
    <div className="hidden md:flex items-center gap-2 shrink-0">
      {cartBtn}
      <Link to={dest} className="h-btn-outline px-4 py-2 text-xs" data-testid="nav-console">My Console</Link>
    </div>
  );
}

export default function PublicLayout() {
  const location = useLocation();
  const { user } = useAuth();
  const { totals, openDrawer } = useCart();
  const [open, setOpen] = useState(false);

  // Close drawer + scroll to top on route change
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const consoleDest = user
    ? (user.role === 'admin' ? '/admin'
        : user.role === 'founder' ? '/console/founder'
        : user.role === 'ambassador' ? '/console/ambassador'
        : '/console/customer')
    : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--heritage-cream)' }}>
      {/* ---------- TOP NAV ---------- */}
      <header
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{ background: 'rgba(246,240,228,0.92)', borderColor: 'rgba(75,24,56,0.15)' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 md:gap-3 shrink-0" data-testid="nav-logo">
            <DahliaMark size={44} />
            <div className="flex flex-col leading-none">
              <span className="h-serif text-base md:text-lg tracking-[0.24em] md:tracking-[0.28em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</span>
              <span className="h-eyebrow mt-1 text-[10px] md:text-xs">Apparel</span>
            </div>
          </Link>

          {/* Desktop nav (md and up) */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8 min-w-0">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `text-sm tracking-wide whitespace-nowrap transition-colors ${isActive ? 'font-medium' : 'opacity-75 hover:opacity-100'}`
                }
                style={{ color: 'var(--heritage-plum)' }}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <DesktopConsoleShortcut />

          {/* Mobile: cart + burger */}
          <div className="md:hidden flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openDrawer}
              className="relative p-1"
              aria-label="Open shopping bag"
              data-testid="nav-cart-mobile"
              style={{ color: 'var(--heritage-plum)' }}
            >
              <ShoppingBag size={22} />
              {totals.itemCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-medium flex items-center justify-center"
                  style={{ background: 'var(--heritage-plum)', color: '#F6F0E4' }}
                >
                  {totals.itemCount}
                </span>
              )}
            </button>
            <button
              className="p-1 -mr-1"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-menu-open"
              style={{ color: 'var(--heritage-plum)' }}
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* ---------- CART DRAWER ---------- */}
      <CartDrawer />

      {/* ---------- MOBILE SIDEBAR DRAWER ---------- */}
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        aria-hidden={!open}
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'rgba(75,24,56,0.45)', backdropFilter: 'blur(3px)' }}
      />
      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-label="Site navigation"
        aria-hidden={!open}
        data-testid="mobile-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-[82vw] max-w-[340px] md:hidden flex flex-col shadow-2xl transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{
          background: 'var(--heritage-cream)',
          borderLeft: '3px solid var(--heritage-plum)',
        }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <Link to="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
            <DahliaMark size={40} />
            <div className="flex flex-col leading-none">
              <span className="h-serif text-base tracking-[0.22em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</span>
              <span className="h-eyebrow mt-1 text-[10px]">Apparel</span>
            </div>
          </Link>
          <button
            className="p-1 -mr-1"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            data-testid="nav-menu-close"
            style={{ color: 'var(--heritage-plum)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Drawer links */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg h-serif text-lg transition-colors ${isActive ? 'bg-[rgba(75,24,56,0.09)] font-medium' : 'hover:bg-[rgba(75,24,56,0.05)]'}`
              }
              style={{ color: 'var(--heritage-plum)' }}
              data-testid={`drawer-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {n.label}
            </NavLink>
          ))}

          <div className="h-px my-3 mx-4" style={{ background: 'rgba(75,24,56,0.15)' }} />

          <div className="px-4">
            <div className="h-eyebrow mb-2">Information</div>
            <div className="flex flex-col gap-1 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <Link to="/info/size-guide" onClick={() => setOpen(false)} className="py-1.5 opacity-80 hover:opacity-100">Size Guide</Link>
              <Link to="/info/track-order" onClick={() => setOpen(false)} className="py-1.5 opacity-80 hover:opacity-100">Track Order</Link>
              <Link to="/info/faq" onClick={() => setOpen(false)} className="py-1.5 opacity-80 hover:opacity-100">FAQs</Link>
              <Link to="/legal/shipping" onClick={() => setOpen(false)} className="py-1.5 opacity-80 hover:opacity-100">Shipping and Returns</Link>
            </div>
          </div>
        </nav>

        {/* Drawer footer CTAs */}
        <div className="p-4 border-t space-y-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          {user ? (
            <Link
              to={consoleDest}
              onClick={() => setOpen(false)}
              className="h-btn w-full text-center inline-flex items-center justify-center"
              data-testid="drawer-console"
            >
              <UserCircle2 size={16} className="mr-2" /> My Console
            </Link>
          ) : (
            <>
              <Link
                to="/store"
                onClick={() => setOpen(false)}
                className="h-btn w-full text-center inline-block"
                data-testid="drawer-shop"
              >
                Shop the Store
              </Link>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="h-btn-outline w-full text-center inline-flex items-center justify-center"
                data-testid="drawer-login"
              >
                <LogIn size={14} className="mr-2" /> Log in
              </Link>
            </>
          )}
          <p className="text-[11px] opacity-70 text-center pt-2" style={{ color: 'var(--heritage-plum)' }}>
            Heritage Dahlias NZ Limited
          </p>
        </div>
      </aside>

      {/* ---------- MAIN ---------- */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ---------- FOOTER ---------- */}
      <footer className="mt-20 border-t" style={{ borderColor: 'rgba(75,24,56,0.2)', background: '#EFE6D2' }}>
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <HeritageSeal size={72} />
            <p className="text-xs mt-4 leading-relaxed opacity-80" style={{ color: 'var(--heritage-plum)' }}>
              Grown in the field. Sewn with intention. Small batch heritage apparel from Aotearoa.
            </p>
            <p className="text-xs mt-3 opacity-70" style={{ color: 'var(--heritage-plum)' }}>
              Heritage Dahlias NZ Limited<br/>Aotearoa, New Zealand<br/>hello@heritageapparel.co.nz
            </p>
          </div>
          <div>
            <div className="h-eyebrow mb-3">Shop</div>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <li><Link to="/catalog" className="hover:underline">Catalogue</Link></li>
              <li><Link to="/collection" className="hover:underline">Collection</Link></li>
              <li><Link to="/store" className="hover:underline">Store</Link></li>
              <li><Link to="/info/track-order" className="hover:underline">Track Order</Link></li>
              <li><Link to="/info/size-guide" className="hover:underline">Size Guide</Link></li>
              <li><Link to="/info/faq" className="hover:underline">FAQs</Link></li>
            </ul>
          </div>
          <div>
            <div className="h-eyebrow mb-3">Support</div>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <li><Link to="/contact" className="hover:underline">Contact</Link></li>
              <li><Link to="/ambassador/apply" className="hover:underline">Become a Heritage Ambassador</Link></li>
              <li><Link to="/legal/shipping" className="hover:underline">Shipping</Link></li>
              <li><Link to="/legal/returns" className="hover:underline">Returns</Link></li>
              <li><Link to="/legal/refund" className="hover:underline">Refund</Link></li>
              <li><Link to="/legal/warranty" className="hover:underline">Warranty and Care</Link></li>
              <li><Link to="/about" className="hover:underline">About Heritage</Link></li>
            </ul>
          </div>
          <div>
            <div className="h-eyebrow mb-3">Legal</div>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <li><Link to="/legal/terms" className="hover:underline">Terms and Conditions</Link></li>
              <li><Link to="/legal/privacy" className="hover:underline">Privacy Policy</Link></li>
              <li><Link to="/legal/cookies" className="hover:underline">Cookie Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:underline">Terms</Link></li>
              <li><Link to="/legal/collective-terms" className="hover:underline">Heritage Collective Terms</Link></li>
              <li><Link to="/legal/intellectual-property" className="hover:underline">Intellectual Property</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>
              &copy; {new Date().getFullYear()} Heritage Dahlias NZ Limited. All rights reserved.
            </div>
            <div className="text-xs opacity-70 flex items-center gap-4" style={{ color: 'var(--heritage-plum)' }}>
              <Link to="/console/founder" className="hover:underline">Founder</Link>
              <Link to="/console/customer" className="hover:underline">Customer</Link>
              <Link to="/console/ambassador" className="hover:underline">Ambassador</Link>
              <Link to="/login?admin=1" className="hover:underline">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
